/**
 * שידור לרשויות — upload, validation, transmission.
 *
 * The honesty constraint from the master plan lives on this screen. Transmission goes
 * through `TaxAuthorityGateway`, and until the שע״ם registration completes that is a fake.
 * So "שודר" is only ever set from a gateway response carrying a confirmation number, and
 * the UI says which environment it is talking to. The legacy's transmitted flag meant
 * "someone clicked a button", and that is the exact failure being designed out.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  AUTHORITIES,
  formatDateTime,
  formatIls,
  formatMonthName,
  type AuthorityKey,
} from "@onecpa/shared";
import {
  Badge,
  Button,
  type Column,
  DataTable,
  EmptyState,
  ErrorState,
  KpiStrip,
  PageHeader,
  StatusBadge,
} from "@onecpa/ui";
import { api } from "../api/client.js";
import { usePeriod } from "../app/hooks.js";
import { useRole, useSession } from "../app/session.js";
import { AuthorityFileUpload } from "./AuthorityFileUpload.js";

interface ReportRow {
  id: string;
  clientId: string;
  clientName: string;
  authority: string;
  periodYear: number;
  periodMonth: number;
  status: string;
  validationSummary: string | null;
  validationErrorCount: number;
  transmittedAt: string | null;
  confirmationNumber: string | null;
  isBiMonthly: boolean;
  amountToPay: string | null;
  sourceFileName: string | null;
}

export function AuthorityReports() {
  const session = useSession();
  const { isManager } = useRole();
  const queryClient = useQueryClient();
  const { period, label, next, previous, goTo } = usePeriod();
  const [uploading, setUploading] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["authority-reports", session.orgId, period.year, period.month],
    queryFn: () => api.reporting.authorityReports(session.orgId, period),
  });

  // Only for the picker in the upload dialog, so the whole list is wanted rather
  // than the paginated first page.
  const { data: clientList } = useQuery({
    queryKey: ["clients", "picker", session.orgId],
    queryFn: () => api.clients.list(session.orgId, { limit: 200 }),
  });

  const transmit = useMutation({
    mutationFn: (id: string) => api.reporting.transmit(session.orgId, id),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["authority-reports"] });
      session.toast(
        result.report?.confirmationNumber
          ? `שודר · אסמכתא ${result.report.confirmationNumber}`
          : "השידור נדחה — יש לבדוק את הדוח",
        result.report?.confirmationNumber ? "success" : "danger",
      );
    },
    onError: (mutationError: Error) => session.toast(mutationError.message, "danger"),
  });

  const rows = data?.reports ?? [];

  const columns: Column<ReportRow>[] = [
    {
      key: "client",
      header: "לקוח",
      sticky: true,
      width: "210px",
      cell: (row) => (
        <Link
          to="/clients/$clientId"
          params={{ clientId: row.clientId }}
          className="link-underline text-sm font-semibold text-ink"
        >
          {row.clientName}
        </Link>
      ),
    },
    {
      key: "authority",
      header: "רשות",
      cell: (row) => (
        <span className="text-xs text-ink-muted">
          {AUTHORITIES[row.authority as AuthorityKey]?.label ?? row.authority}
        </span>
      ),
    },
    {
      key: "period",
      header: "תקופה",
      cell: (row) => (
        <span className="text-xs text-ink-muted">
          {formatMonthName(row.periodMonth)} {row.periodYear}
          {row.isBiMonthly && <span className="text-ink-faint"> · דו-חודשי</span>}
        </span>
      ),
    },
    {
      key: "amount",
      header: "לתשלום",
      align: "end",
      hideOnSmall: true,
      cell: (row) =>
        row.amountToPay && Number(row.amountToPay) !== 0 ? (
          <bdi className="serif tnum text-sm text-ink">
            {formatIls(Number(row.amountToPay))}
          </bdi>
        ) : (
          <span className="text-xs text-ink-faint">—</span>
        ),
    },
    {
      key: "validation",
      header: "ולידציה",
      cell: (row) => (
        <span className="flex items-center gap-2">
          {row.validationErrorCount > 0 ? (
            <Badge tone="danger">{row.validationErrorCount} שגיאות</Badge>
          ) : null}
          <span className="truncate text-2xs text-ink-faint">
            {row.validationSummary ?? "—"}
          </span>
        </span>
      ),
    },
    {
      key: "confirmation",
      header: "אסמכתא",
      hideOnSmall: true,
      cell: (row) =>
        row.confirmationNumber ? (
          <span className="flex flex-col">
            <bdi className="tnum text-xs text-ink" dir="ltr">
              {row.confirmationNumber}
            </bdi>
            {row.transmittedAt && (
              <bdi className="tnum text-2xs text-ink-faint">
                {formatDateTime(row.transmittedAt)}
              </bdi>
            )}
          </span>
        ) : (
          <span className="text-xs text-ink-faint">—</span>
        ),
    },
    {
      key: "status",
      header: "מצב",
      align: "end",
      cell: (row) => (
        <span className="flex items-center justify-end gap-2">
          <StatusBadge vocabulary="authorityReport" status={row.status} />
          {isManager && row.status !== "transmitted" && (
            <Button
              size="sm"
              variant={row.status === "error" ? "secondary" : "primary"}
              disabled={row.status === "error"}
              title={row.status === "error" ? "יש לתקן את שגיאות הוולידציה לפני שידור" : undefined}
              loading={transmit.isPending && transmit.variables === row.id}
              onClick={() => transmit.mutate(row.id)}
            >
              שידור
            </Button>
          )}
        </span>
      ),
    },
  ];

  if (error) {
    return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  }

  const counts = {
    total: rows.length,
    uploaded: rows.filter((row) => row.status === "uploaded").length,
    validated: rows.filter((row) => row.status === "validated").length,
    errors: rows.filter((row) => row.status === "error").length,
    transmitted: rows.filter((row) => row.status === "transmitted").length,
  };

  return (
    <>
      <PageHeader
        title="שידור לרשויות"
        period={
          <span className="inline-flex items-center gap-1.5">
            <button type="button" onClick={previous} aria-label="חודש קודם" className="px-1">
              ‹
            </button>
            {label}
            <button type="button" onClick={next} aria-label="חודש הבא" className="px-1">
              ›
            </button>
          </span>
        }
        action={
          <Button variant="primary" icon="upload" onClick={() => setUploading(true)}>
            העלאת קובץ
          </Button>
        }
      />

      <AuthorityFileUpload
        open={uploading}
        onClose={() => setUploading(false)}
        clients={clientList?.clients ?? []}
        onStored={goTo}
      />

      <KpiStrip
        loading={isLoading}
        items={[
          { label: "סה״כ דוחות", value: counts.total },
          { label: "הועלו", value: counts.uploaded, tone: "pending" },
          { label: "עברו ולידציה", value: counts.validated },
          { label: "שגיאות", value: counts.errors, tone: counts.errors > 0 ? "red" : "ink" },
          { label: "שודרו", value: counts.transmitted, tone: "ledger" },
        ]}
      />

      <div className="mx-5 mt-4 flex items-start gap-2 rounded-card border border-warning-ink/30 bg-warning-bg px-3.5 py-2.5">
        <span className="text-xs text-warning-ink">
          החיבור לשע״ם פועל כרגע מול שער דמה מקומי. «שודר» נקבע רק מתשובת השער עם אסמכתא, ולכן שידור
          מקומי אינו דיווח בפועל לרשות.
        </span>
      </div>

      <div className="p-5">
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={isLoading}
          caption="דוחות לרשויות לפי לקוח"
          footnote="דוח עם שגיאות ולידציה אינו ניתן לשידור עד שיתוקן · אסמכתא היא האישור מהרשות"
          empty={
            <EmptyState
              title="אין דוחות לחודש זה"
              description='דוח נוצר כשמעלים קובץ מע"מ מ-Finbot או PCN874. השתמשו בכפתור «העלאת קובץ».'
              icon="file-text"
            />
          }
        />
      </div>
    </>
  );
}
